import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, Moon, Sun } from "lucide-react";
import { Logo } from "./Logo";
import { DonateButton } from "./DonateButton";
import { useTheme } from "../hooks/useTheme";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About", id: "about" },
  { to: "/program", label: "Priced Out by Lowell", id: "program" },
  { to: "/census", label: "Census", id: "census" },
  { to: "/resources", label: "Resources", id: "resources" },
  { to: "/transparency", label: "Transparency", id: "transparency" },
  { to: "/get-involved", label: "Get Involved", id: "get-involved" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const linkCls = ({ isActive }) =>
    `whitespace-nowrap rounded-full px-2.5 py-2 text-[0.82rem] font-semibold no-underline transition-colors hover:bg-primary/10 ${
      isActive
        ? "bg-primary/10 text-primary dark:text-accent"
        : "text-foreground/80"
    }`;

  return (
    <header
      className={`sticky top-0 z-40 border-b transition-all duration-300 ${
        scrolled || open
          ? "border-border bg-card/85 backdrop-blur-xl"
          : "border-transparent bg-background/70 backdrop-blur-sm"
      }`}
      data-testid="site-header"
    >
      <div className="wrap flex items-center justify-between gap-3 py-3">
        <Logo />
        <nav
          className="hidden items-center gap-0.5 xl:flex"
          aria-label="Main"
          data-testid="main-nav"
        >
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={linkCls}
              data-testid={`nav-${l.id}-link`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2 xl:ml-0">
          <button
            onClick={toggle}
            aria-label="Toggle color theme"
            data-testid="theme-toggle-button"
            className="btn btn-ghost btn-sm !rounded-full !border-border !px-3 !text-foreground"
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </button>
          <span className="hidden sm:block">
            <DonateButton size="btn-sm" />
          </span>
          <button
            className="btn btn-ghost btn-sm !rounded-full !border-border !px-3 !text-foreground xl:hidden"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav
          id="mobile-nav"
          className="border-t border-border bg-card/95 backdrop-blur-xl xl:hidden"
          aria-label="Mobile"
          data-testid="mobile-nav"
        >
          <div className="wrap flex flex-col py-3">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `rounded-xl px-3 py-3 text-base font-semibold no-underline ${
                    isActive
                      ? "bg-primary/10 text-primary dark:text-accent"
                      : "text-foreground/85"
                  }`
                }
                data-testid={`nav-${l.id}-link-mobile`}
              >
                {l.label}
              </NavLink>
            ))}
            <span className="mt-2 sm:hidden">
              <DonateButton size="btn-sm" testid="donate-mobile-button" />
            </span>
          </div>
        </nav>
      )}
    </header>
  );
};
